import { MemberItemEntity } from "@/modules/members/entities/memberEntity"
import type { MemberActiveOfferDto, MemberItemDto } from "@/modules/members/gateway/memberDto"
import { MemberStatus } from "@/modules/members/gateway/memberDto"
import dayjs from "dayjs"

export class MemberItemBuilder {
  private dto: MemberItemDto

  static create() {
    return new MemberItemBuilder()
  }

  protected constructor() {
    this.dto = {
      id: "member-default",
      avatar: null,
      firstname: "John",
      lastname: "Doe",
      username: "john.doe",
      status: MemberStatus.ACTIVE,
      note: null,
      lastVisited: dayjs().subtract(1, "day").toISOString(),
      noShowPastMonth: 0,
      activeOffers: [],
      archivedDate: null,
      inscriptionDate: dayjs().subtract(6, "month").toISOString(),
      archived: false,
      lastMembership: null,
    }
  }

  withId(id: string) {
    this.dto.id = id
    return this
  }

  withFirstname(firstname: string) {
    this.dto.firstname = firstname
    return this
  }

  withLastname(lastname: string) {
    this.dto.lastname = lastname
    return this
  }

  withName(firstname: string, lastname: string) {
    this.dto.firstname = firstname
    this.dto.lastname = lastname
    this.dto.username = `${firstname.toLowerCase()}.${lastname.toLowerCase()}`
    return this
  }

  withUsername(username: string) {
    this.dto.username = username
    return this
  }

  withStatus(status: MemberStatus) {
    this.dto.status = status
    return this
  }

  asActive() {
    this.dto.status = MemberStatus.ACTIVE
    return this
  }

  asProspect() {
    this.dto.status = MemberStatus.PROSPECT
    return this
  }

  asTerminated() {
    this.dto.status = MemberStatus.TERMINATED
    return this
  }

  asDropIn() {
    this.dto.status = MemberStatus.DROP_IN
    return this
  }

  withNote(note: string) {
    this.dto.note = note
    return this
  }

  withLastVisited(date: string) {
    this.dto.lastVisited = date
    return this
  }

  withLastVisitedDaysAgo(days: number) {
    this.dto.lastVisited = dayjs().subtract(days, "day").toISOString()
    return this
  }

  withNoShowPastMonth(count: number) {
    this.dto.noShowPastMonth = count
    return this
  }

  withActiveOffers(offers: MemberActiveOfferDto[]) {
    this.dto.activeOffers = offers
    return this
  }

  withOffer(offerName: string) {
    this.dto.activeOffers.push({ offerName, endValidity: null, endSubscription: null, subscriptionStatus: null })
    return this
  }

  withInscriptionDate(date: string) {
    this.dto.inscriptionDate = date
    return this
  }

  withInscriptionDaysAgo(days: number) {
    this.dto.inscriptionDate = dayjs().subtract(days, "day").toISOString()
    return this
  }

  asArchived() {
    this.dto.archived = true
    this.dto.archivedDate = dayjs().subtract(30, "day").toISOString()
    return this
  }

  withLastMembership(membership: string) {
    this.dto.lastMembership = membership
    return this
  }

  build(): MemberItemEntity {
    return MemberItemEntity.fromItemDto(this.dto)
  }

  buildDto(): MemberItemDto {
    return { ...this.dto }
  }
}
